import { useCallback, useEffect, useRef, useState } from 'react';
import type { SleepMode } from '../ListenContext.js';

/** Margen para considerar terminado el capítulo (el último `timeupdate` puede quedar corto). */
export const FINISH_EPS = 0.5;
/** Segundos del countdown visible antes de auto-avanzar. */
export const AUTO_ADVANCE_SECS = 15;
const TICK_MS = 250;

export interface UseAutoAdvanceReturn {
  /** Segundos restantes del countdown (null = inactivo). */
  countdown: number | null;
  /** Cancela el countdown (cualquier interacción con el panel de fin). */
  cancelAutoAdvance: () => void;
}

/**
 * Auto-avance al próximo capítulo al terminar (pref `autoAdvance`, panel de
 * fin 19.G). Vive en el PROVIDER, no en el player screen: tiene que funcionar
 * aunque el player esté minimizado (barra global) o la pantalla apagada.
 *
 * Dos decisiones anti-throttling (bug original: "no avanza solo"):
 *   1. El countdown usa un DEADLINE absoluto (`Date.now`) y un interval que
 *      computa el restante, en vez de encadenar timeouts de 1s. Al terminar el
 *      audio la pestaña deja de ser "audible" y el browser throttlea los
 *      timers de background — la cadena de ticks nunca llegaba a 0. Un tick
 *      tardío contra el deadline igual dispara.
 *   2. Si la página está OCULTA (pantalla bloqueada / otra app) no hay panel
 *      que mirar ni tap que esperar: encadenamos directo, como un reproductor
 *      nativo. Cubre también el caso de ocultarse con el countdown ya armado.
 *
 * Política de cobro: con `autoAdvance` ON el avance SÍ desbloquea/cobra el
 * próximo capítulo si es pago (el toggle del sheet de Reproducción es el
 * consentimiento explícito; el cobro lo hace `openChapter`→`loadRendition`).
 * Antes el auto-avance se frenaba en capítulos pagos ("no cobra solo"); se
 * cambió para habilitar el caso "dejar sonando para dormir".
 */
export function useAutoAdvance(args: {
  pos: number;
  duration: number;
  autoAdvance: boolean;
  sleepMode: SleepMode | null;
  cancelSleep: () => void;
  hasNext: boolean;
  goNextChapter: () => void;
}): UseAutoAdvanceReturn {
  const { pos, duration, autoAdvance, hasNext } = args;
  const finished = duration > 0 && pos >= duration - FINISH_EPS;

  const [countdown, setCountdown] = useState<number | null>(null);
  const deadlineRef = useRef<number | null>(null);

  // `goNextChapter`/`cancelSleep` por ref: sus identidades cambian con el
  // catálogo/ref y no queremos que re-armen el efecto. `sleepMode` TAMBIÉN por
  // ref, a propósito: al frenar por 'eoc', `cancelSleep()` lo vuelve null y un
  // efecto reactivo re-armaría el countdown (avanzaría igual, anulando el
  // temporizador de sueño).
  const goNextRef = useRef(args.goNextChapter);
  goNextRef.current = args.goNextChapter;
  const sleepRef = useRef({ mode: args.sleepMode, cancel: args.cancelSleep });
  sleepRef.current = { mode: args.sleepMode, cancel: args.cancelSleep };

  const cancelAutoAdvance = useCallback((): void => {
    deadlineRef.current = null;
    setCountdown(null);
  }, []);

  const fire = useCallback((): void => {
    deadlineRef.current = null;
    setCountdown(null);
    goNextRef.current();
  }, []);

  // Armado al terminar el capítulo. Re-encender el toggle con el panel abierto
  // re-arma el countdown (feedback inmediato). `nextUp`/`unlockedKeys` en deps:
  // si el próximo capítulo se desbloquea durante el panel de fin, el gate se
  // re-evalúa.
  useEffect(() => {
    if (!finished) {
      cancelAutoAdvance();
      return;
    }
    if (sleepRef.current.mode === 'eoc') {
      // Sueño "fin del capítulo": frenar acá ES el objetivo. cancelSleep
      // resetea el modo sin re-disparar este efecto (sleepMode no es dep).
      sleepRef.current.cancel();
      cancelAutoAdvance();
      return;
    }
    if (!hasNext || !autoAdvance) {
      cancelAutoAdvance();
      return;
    }
    // El próximo capítulo pago ya NO frena el avance: `goNextChapter` lo
    // desbloquea/cobra (el toggle fue el consentimiento). Ver doc del hook.
    if (document.visibilityState === 'hidden') {
      fire();
      return;
    }
    deadlineRef.current = Date.now() + AUTO_ADVANCE_SECS * 1000;
    setCountdown(AUTO_ADVANCE_SECS);
  }, [finished, autoAdvance, hasNext, cancelAutoAdvance, fire]);

  // Tick contra el deadline + avance inmediato si la página se oculta.
  const armed = countdown !== null;
  useEffect(() => {
    if (!armed) return;
    const tick = (): void => {
      const dl = deadlineRef.current;
      if (dl === null) return;
      const left = Math.ceil((dl - Date.now()) / 1000);
      if (left <= 0) fire();
      else setCountdown(left);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden' && deadlineRef.current !== null) fire();
    };
    const id = window.setInterval(tick, TICK_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [armed, fire]);

  return { countdown, cancelAutoAdvance };
}
